// Absolute grid placement for the My Page canvas (Android-style).
//
// The canvas was an iPhone-style packed flow (`grid-flow-row-dense`, array order,
// no gaps). It is now an Android-style fixed grid: every widget has an explicit
// `(x, y)` cell position and can sit anywhere — gaps are allowed and preserved.
// This is the pure, unit-tested geometry: rectangle overlap, bounds clamping,
// first-free-cell search, and the load-time `resolveLayout` that keeps stored
// positions and auto-places any widget that has none (legacy layouts, freshly
// added widgets) into the first free cell.
//
// The grid is `GRID_COLS` wide; rows grow down to `MAX_ROWS`. Positions are
// 1-based (`x` 1..GRID_COLS, `y` 1..MAX_ROWS), matching the CSS `col-start` /
// `row-start` the renderer emits.

import { MAX_ROWS, MAX_W } from './widget-sizes';

/** The canvas column count — mirrors the size model's `MAX_W` (the 4-col track). */
export const GRID_COLS = MAX_W;
export { MAX_ROWS };

/** A footprint on the grid: 1-based top-left cell `(x, y)` plus its span `(w, h)`. */
export interface Rect {
    x: number;
    y: number;
    w: number;
    h: number;
}

/** A placed widget: its id plus its resolved footprint. */
export interface Placed extends Rect {
    id: string;
}

/** A widget to lay out: its id + footprint, with an OPTIONAL stored position. */
export interface PlaceInput {
    id: string;
    w: number;
    h: number;
    x?: number;
    y?: number;
}

function clampInt(n: number, lo: number, hi: number): number {
    return Math.max(lo, Math.min(hi, Math.round(n)));
}

/** Do two footprints share any cell? (half-open interval overlap on both axes.) */
export function rectsOverlap(a: Rect, b: Rect): boolean {
    return a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
}

/**
 * Clamp a raw `(x, y)` so the whole `w × h` footprint stays on the grid: `x` so
 * the right edge stays within `GRID_COLS`, `y` so the bottom edge stays within
 * `MAX_ROWS`. Used by drag/resize so a widget can never be placed (or grown) off
 * the grid.
 */
export function clampPosition(
    x: number,
    y: number,
    w: number,
    h: number,
): { x: number; y: number } {
    return {
        x: clampInt(x, 1, Math.max(1, GRID_COLS - w + 1)),
        y: clampInt(y, 1, Math.max(1, MAX_ROWS - h + 1)),
    };
}

/** Does `target` collide with any placed widget other than `selfId`? */
export function collidesWithOthers(
    target: Rect,
    placed: ReadonlyArray<Placed>,
    selfId: string,
): boolean {
    return placed.some((p) => p.id !== selfId && rectsOverlap(target, p));
}

/**
 * The first free cell (scanning top-to-bottom, left-to-right) that fits a
 * `w × h` footprint without overlapping anything occupied. Falls back to the
 * top-left if the grid is full (a degenerate overlap, far beyond a real layout).
 */
export function firstFreeCell(
    occupied: ReadonlyArray<Rect>,
    w: number,
    h: number,
): { x: number; y: number } {
    for (let y = 1; y <= MAX_ROWS; y++) {
        for (let x = 1; x + w - 1 <= GRID_COLS; x++) {
            const candidate: Rect = { x, y, w, h };
            if (!occupied.some((o) => rectsOverlap(candidate, o))) return { x, y };
        }
    }
    return { x: 1, y: 1 };
}

/**
 * Resolve every widget to a concrete position. Widgets with a stored `(x, y)`
 * keep it (clamped on-grid); widgets without one — a legacy layout that predates
 * positions, or a freshly added widget — are auto-placed into the first free
 * cell, in array order. The two-pass order (fixed positions first, then fill)
 * means auto-placed widgets flow around the ones the user has deliberately
 * positioned. Returns one `Placed` per input, in the input order. Pure.
 */
export function resolveLayout(items: ReadonlyArray<PlaceInput>): Placed[] {
    const occupied: Rect[] = [];
    const byId = new Map<string, Placed>();

    for (const it of items) {
        if (it.x != null && it.y != null) {
            const { x, y } = clampPosition(it.x, it.y, it.w, it.h);
            const rect: Rect = { x, y, w: it.w, h: it.h };
            byId.set(it.id, { id: it.id, ...rect });
            occupied.push(rect);
        }
    }
    for (const it of items) {
        if (it.x == null || it.y == null) {
            const { x, y } = firstFreeCell(occupied, it.w, it.h);
            const rect: Rect = { x, y, w: it.w, h: it.h };
            byId.set(it.id, { id: it.id, ...rect });
            occupied.push(rect);
        }
    }

    return items.map((it) => byId.get(it.id) ?? { id: it.id, x: 1, y: 1, w: it.w, h: it.h });
}

/** The lowest occupied row (1-based) across a layout, or 0 when empty. */
export function maxOccupiedRow(placed: ReadonlyArray<Placed>): number {
    return placed.reduce((m, p) => Math.max(m, p.y + p.h - 1), 0);
}
